import { registerView } from '../viewRegistry';
import { interactiveRegistry } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import { listGameAgents, startSimulation, getSimulationStatus } from '../../utils/api';

// ─── State ───────────────────────────────────────────────────────────
let agents = [];
let agentsLoaded = false;
let selectedIdx = 0;
let simId = null;
let simStatus = null;
let lastPoll = 0;

async function ensureAgents() {
  if (agentsLoaded) return;
  try {
    const fetched = await listGameAgents();
    if (Array.isArray(fetched) && fetched.length > 0) {
      agents = fetched;
    } else {
      throw new Error('Empty agents list');
    }
  } catch (err) {
    // Graceful fallback so the view remains usable even if the backend
    // endpoint is down in staging/preview environments.
    console.warn('Game Console: using fallback agent list', err?.message || err);
    agents = [
      {
        name: 'bigboss',
        display_name: 'Big Boss',
        description: 'Laconic and strategic',
      },
    ];
  } finally {
    agentsLoaded = true;
  }
}

function renderGameConsole(ctx, t, w, h) {
  // Clear
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  const buffer = { w, h };
  const safeArea = getCanonicalSafeArea(buffer);

  // Header
  ctx.fillStyle = '#39FF14';
  ctx.font = '16px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText('GAME CONSOLE – BIG BOSS', w / 2, safeArea.top + 4);

  // Load agents list if needed
  ensureAgents();

  // Draw agent list
  const listStartY = safeArea.top + 28;
  const lineH = 16 * 1.4;
  if (!agentsLoaded) {
    ctx.fillText('Loading agents…', w / 2, listStartY);
  } else if (agents.length === 0) {
    ctx.fillText('No agents found', w / 2, listStartY);
  } else {
    ctx.textAlign = 'left';
    const startX = safeArea.left + 8;
    agents.forEach((agent, i) => {
      const y = listStartY + i * lineH;
      ctx.fillStyle = i === selectedIdx ? '#39FF14' : '#1aff66';
      ctx.fillText(`${i === selectedIdx ? '>' : ' '} ${agent.name || agent}`, startX, y);
      // Register click area
      interactiveRegistry.register({
        id: `agent-${i}`,
        x: startX,
        y: y,
        w: safeArea.width,
        h: lineH,
        onClick: () => {
          selectedIdx = i;
        },
      });
    });
  }

  // Draw RUN button
  const btnText = simId ? 'RUNNING…' : 'RUN SIMULATION';
  const scale = 1.5;
  const measure = DopeTextRenderer.measureDOSText(btnText, scale);
  const btnW = measure.width + 20;
  const btnH = measure.height + 16;
  const btnX = Math.round(safeArea.left + (safeArea.width - btnW) / 2);
  const btnY = Math.round(safeArea.bottom - btnH - 12);

  // Button bg & stroke
  ctx.fillStyle = '#000';
  ctx.fillRect(btnX, btnY, btnW, btnH);
  ctx.strokeStyle = '#39FF14';
  ctx.lineWidth = 3;
  ctx.strokeRect(btnX + 1, btnY + 1, btnW - 2, btnH - 2);

  // Label
  DopeTextRenderer.renderDOSText(
    ctx,
    btnText,
    btnX + 10,
    btnY + Math.round((btnH - measure.height) / 2),
    scale,
  );

  // Register button
  interactiveRegistry.register({
    id: 'run-sim-btn',
    x: btnX,
    y: btnY,
    w: btnW,
    h: btnH,
    onClick: async () => {
      if (simId || !agentsLoaded || agents.length === 0) return;
      try {
        const res = await startSimulation(agents[selectedIdx].name || agents[selectedIdx], {});
        simId = res.simulation_id;
        simStatus = null;
        lastPoll = 0;
      } catch (err) {
        console.error('Failed to start simulation', err);
      }
    },
    focusable: true,
  });

  // Poll status every 2s
  if (simId && t - lastPoll > 2) {
    lastPoll = t;
    getSimulationStatus(simId)
      .then((s) => {
        simStatus = s;
        if (s?.state === 'completed' || s?.state === 'failed') {
          simId = null;
        }
      })
      .catch((err) => {
        console.error('Status poll failed', err);
        simId = null;
      });
  }

  // Draw status
  if (simStatus) {
    ctx.textAlign = 'left';
    const lines = [
      `STATUS: ${simStatus.state}`,
      `STEP:   ${simStatus.current_step || 0}/${simStatus.total_steps || '?'}`,
      `REWARD: ${simStatus.reward || '…'}`,
    ];
    lines.forEach((ln, i) => {
      ctx.fillText(ln, safeArea.left + 8, btnY - 60 + i * 14);
    });
  }
}

registerView('game_console', renderGameConsole); 